import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function ProfitDayWiseReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(DateTimeHelpers.getFiveDateEarlier());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    date: 'Date',
    num_bills: 'No. Invoice',
    total: {
      title: 'Invoice Total',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
    tax: {
      title: 'Tax Amount',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.tax || 0);
      },
    },
    cess: {
      title: 'Additional Tax',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.cess || 0);
      },
    },
    purchase_expense: {
      title: 'Purchase Cost',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.purchase_expense || 0);
      },
    },
    expense: {
      title: 'Expense',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.expense || 0);
      },
    },
    damage_value: {
      title: 'Damage',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.damage_value || 0);
      },
    },
    discount: {
      title: 'Discount',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.discount || 0);
      },
    },
    profit: {
      title: 'Profit',
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.profit || 0);
      },
    },
  };

  const arraysAreEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((val) => b.includes(val));
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      !DateTimeHelpers.isSameDate(filterFromDate, prevFilterData.filterFromDate) ||
      !DateTimeHelpers.isSameDate(filterToDate, prevFilterData.filterToDate) ||
      !arraysAreEqual(filterTerminal, prevFilterData.filterTerminal) ||
      !arraysAreEqual(filterSalesperson, prevFilterData.filterSalesperson)
    );
  }, [filterFromDate, filterToDate, filterTerminal, filterSalesperson, prevFilterData]);

  useEffect(() => {
    // getMachineName();
    if (!ShopsHelper.hasProAccess(shop)) return;
    generateSummeryReport();
    getSalespersons();
    getTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Sales Summary Report With Profit'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredItems,
      {
        [tCommon('Total Sales')]: summary.totalSales,
        [tCommon('Total Profit')]: `${shop?.currency || ''} ${summary.totalProfit}`,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${summary.totalAmount}`,
      }
    );
  };

  const generateSummeryReport = () => {
    const { startDate, endDate } = dynamicDateRange('PROFIT_DAY_WISE_REPORT');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadBillSummaryReports({
      startTime: startDate,
      endTime: endDate,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterSalesperson,
      filterTerminal,
    });
  };

  const loadBillSummaryReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('PROFIT_DAY_WISE_REPORT');

    const start = startTime || dynamicStartDate;
    const end = endTime || dynamicEndDate;
    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
      };
      const resp = await BillSummaryReportService.getDayWiseReportWithProfit(data);
      setItems(resp);
      setFilteredItems(resp);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Sales Summary Report With Profit for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredItems, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getBillReportSummary(filteredItems);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'date':
          return tCommon('Total');
        case 'total':
          return summary.totalAmount;
        case 'num_bills':
          return summary.totalSales;
        case 'tax':
          return summary.totalTax;
        case 'cess':
          return summary.totalAdditionalTax;
        case 'purchase_expense':
          return summary.totalExpence;
        case 'expense':
          return summary.totalExpense;
        case 'damage_value':
          return summary.totalDamange;
        case 'discount':
          return summary.totalDiscount;
        case 'profit':
          return summary.totalProfit;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PROFIT-DAY-WISE-${dateTime}.csv`);

    postAlertMessage({
      text: 'Exported to excel successfully',
      type: 'success',
    });
  };

  const _getBillReportSummary = (items) => {
    let totalSales = 0;
    let totalExpence = 0;
    let totalPurchaseCost = 0;
    let totalPurchaseTax = 0;
    let totalAmount = 0;
    let totalProfit = 0;
    let totalTax = 0;
    let totalExpense = 0;
    let totalDamange = 0;
    let totalAdditionalTax = 0;
    let totalDiscount = 0;
    items.forEach((item) => {
      totalSales += item.num_bills;
      totalExpence += item.purchase_expense;
      totalPurchaseCost += item.purchase_cost;
      totalPurchaseTax += item.purchase_tax;
      totalAmount += item.total;
      totalProfit += item.profit;
      totalTax += item.tax;
      totalExpense += item.expense;
      totalDamange += item.damage_value;
      totalAdditionalTax += item.cess;
      totalDiscount += item.discount;
    });

    return {
      totalSales: ShopsHelper.getAmountFormatted(shop, totalSales),
      totalExpence: ShopsHelper.getAmountFormatted(shop, totalExpence),
      totalPurchaseCost: ShopsHelper.getAmountFormatted(shop, totalPurchaseCost),
      totalPurchaseTax: ShopsHelper.getAmountFormatted(shop, totalPurchaseTax),
      totalProfit: ShopsHelper.getAmountFormatted(shop, totalProfit),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalExpense: ShopsHelper.getAmountFormatted(shop, totalExpense),
      totalDamange: ShopsHelper.getAmountFormatted(shop, totalDamange),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
      rawProfit: totalProfit,
    };
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredItems(items.filter((item) => item.date.includes(value.toLowerCase())));
  };

  const baseHeaderData = useMemo(() => {
    return [
      {
        label: tCommon('Date'),
        id: 'date',
        type: 'text',
      },
      {
        label: tCommon('number_of_invoices'),
        id: 'num_bills',
        type: 'text',
      },
      {
        label: tCommon('sales_amount'),
        id: 'total',
        type: 'callback',
        viewRender: (item) => {
          return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total)}`;
        },
      },
      {
        label: tCommon('Tax Amount'),
        id: 'tax',
        type: 'floatAmount',
      },
      {
        label: tCommon('Additional Tax'),
        id: 'cess',
        type: 'floatAmount',
      },
      {
        label: tCommon('Purchase Cost'),
        id: 'purchase_expense',
        type: 'callback',
        viewRender: (item) => {
          if (item.purchase_expense === '' || isNaN(item.purchase_expense)) return item.purchase_expense;
          return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.purchase_expense)}`;
        },
      },
      {
        label: tCommon('Discount'),
        id: 'discount',
        type: 'floatAmount',
      },
      {
        label: `${tCommon('Profit')}*`,
        id: 'profit',
        type: 'callback',
        viewRender: (item) => {
          if (item.profit === '' || isNaN(item.profit)) return item.profit;
          return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.profit)}`;
        },
      },
    ];
  }, [shop, tCommon]);

  const expenseAndDamageColumns = useMemo(() => {
    return [
      {
        label: tCommon('Expense'),
        id: 'expense',
        type: 'callback',
        viewRender: (item) => {
          if (item.expense === '' || isNaN(item.expense)) return item.expense;
          return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.expense)}`;
        },
      },
      {
        label: tCommon('Damage'),
        id: 'damage_value',
        type: 'callback',
        viewRender: (item) => {
          if (item.damage_value === '' || isNaN(item.damage_value)) return item.damage_value;
          return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.damage_value)}`;
        },
      },
    ];
  }, [shop, tCommon]);

  const headerData = useMemo(() => {
    const shouldAdd = filterTerminal.length === 0 && filterSalesperson.length === 0;
    if (shouldAdd) {
      const newHeaders = [...baseHeaderData];
      newHeaders.splice(6, 0, ...expenseAndDamageColumns);
      return newHeaders;
    }
    return baseHeaderData;
  }, [filterTerminal.length, filterSalesperson.length, baseHeaderData, expenseAndDamageColumns]);

  const summary = _getBillReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>

        {ShopsHelper.hasProAccess(shop) && (
          <>
            <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Print Report')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={handlePrintReport}
              >
                <PrintIcon className={styles.actionBtnIcon} />
                {tCommon('Print')}
              </Button>
            </div>
            <div style={{ justifyContent: 'flex-end' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Export As')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={toCsvBtnPressed}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('CSV')}
              </Button>
            </div>
          </>
        )}
        {!ShopsHelper.hasProAccess(shop) && (
          <div style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              style={{ backgroundColor: '#1d6df2', color: '#fff', borderRadius: '50px', textTransform: 'none', fontWeight: 600 }}
              onClick={() => window.dispatchEvent(new CustomEvent('open-plan-price'))}
            >
              Upgrade Plan
            </Button>
          </div>
        )}
      </div>

      {!ShopsHelper.hasProAccess(shop) && (
        <RestrictionInfo title={tCommon('Feature not available')} content={tCommon('Upgrade day wise')} />
      )}

      {ShopsHelper.hasProAccess(shop) && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tReport('Sales Summary Report With Profit for {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                    to: DateTimeHelpers.convertDateToDMY(filterToDate),
                  })}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      handleSearch(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tCommon('Search by date')}
                  />
                  <Tooltip title="Search" placement="bottom">
                    <SearchIcon className={styles.searchIcon} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange
                  setFilterFromDate={setFilterFromDate}
                  setFilterToDate={setFilterToDate}
                  storageKey="PROFIT_DAY_WISE_REPORT"
                />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Terminal')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterTerminal}
                      onChange={(e) => setFilterTerminal(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterTerminal) => {
                        if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterTerminal.map((id) => terminals.find((tr) => tr.id === id).name);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {terminals.map((terminal) => (
                        <MenuItem key={terminal.id} value={terminal.id}>
                          <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                          <ListItemText primary={terminal.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Salesperson')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterSalesperson}
                      onChange={(e) => setFilterSalesperson(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterSalesperson) => {
                        if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterSalesperson.map(
                          (id) => salesPersons.find((person) => person.id === id).name
                        );
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {salesPersons.map((salesperson) => (
                        <MenuItem key={salesperson.id} value={salesperson.id}>
                          <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                          <ListItemText primary={salesperson?.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Generate Report')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generateSummeryReport}
                    disabled={isShowGenerateButton === false || loadingIndicator}
                  >
                    {loadingIndicator ? (
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    ) : (
                      <SyncIcon className={styles.actionBtnIcon} />
                    )}
                    {tCommon('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {isShowGenerateButton === null ? (
            <Info
              title={tReport('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : (
            items &&
            (filteredItems && filteredItems.length ? (
              <>
                {(filterSalesperson.length > 0 || filterTerminal.length) > 0 && (
                  <Info
                    severity="warning"
                    content={tReport('Current filter: Day-wise profit excluding shop expenses and stock damage')}
                  />
                )}

                <div className={styles.summaryContainer}>
                  <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                    <span className={styles.cardTitle}>{tCommon('Total Amount')}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalAmount}`}</div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Total Invoices')}:{' '}
                        <strong className={styles.detailsValue}>{summary.totalSales}</strong>
                      </span>
                    </div>
                  </div>

                  <div
                    className={`${styles.summaryCard} ${summary.rawProfit >= 0 ? styles.cardGreen : styles.cardRed}`}
                  >
                    <span className={styles.cardTitle}>{tCommon('Profit')}</span>
                    <div className={styles.cardValue} style={{ color: summary.rawProfit >= 0 ? '#2e7d32' : '#c62828' }}>
                      {`${shop?.currency || ''} ${summary.totalProfit}`}
                    </div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Tax Amount')}:{' '}
                        <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${summary.totalTax}`}</strong>
                      </span>
                    </div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
                    <span className={styles.cardTitle}>{tCommon('Purchase Cost')}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalExpence}`}</div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Purchase Tax')}:{' '}
                        <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${
                          summary.totalPurchaseTax
                        }`}</strong>
                      </span>
                    </div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardPurple}`}>
                    <span className={styles.cardTitle}>{tCommon('Expense')}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalExpense}`}</div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Damage')}:{' '}
                        <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${
                          summary.totalDamange
                        }`}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.tableContainer}>
                  <Grayout open={isShowGenerateButton} />
                  <DataTable
                    key={filterTerminal.length + '-' + filterSalesperson.length}
                    columns={headerData}
                    rows={filteredItems ? filteredItems : items}
                    rowKey="id"
                    summary={{
                      date: tCommon('Total'),
                      num_bills: `${summary.totalSales}`,
                      purchase_expense: `${shop?.currency || ''} ${summary.totalExpence}`,
                      tax: `${shop?.currency || ''} ${summary.totalTax}`,
                      cess: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
                      expense: `${shop?.currency || ''} ${summary.totalExpense}`,
                      damage_value: `${shop?.currency || ''} ${summary.totalDamange}`,
                      discount: `${shop?.currency || ''}  ${summary.totalDiscount}`,
                      profit: `${shop?.currency || ''} ${summary.totalProfit}`,
                      total: `${shop?.currency || ''} ${summary.totalAmount}`,
                    }}
                  />
                </div>
              </>
            ) : (
              !loadingIndicator && (
                <Info
                  severity="warning"
                  title={tReport('Oh no, there are no sales matching to your filter')}
                  content={tReport(
                    'There are no sales matching to your filter. Please verify your inputs to generate report.'
                  )}
                />
              )
            ))
          )}
        </>
      )}

      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(ProfitDayWiseReport);
